import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, Facebook, Star } from 'lucide-react';
import { trackClick } from '../../utils/analytics';
import { Button } from '../ui/button';
import ntxLogo from '../../assets/ntx-logo.png';

const navItems = [
  {
    label: 'Fencing', children: [
      { label: 'Pipe Fencing', href: '/pipe-fencing' },
      { label: 'No-Climb Fence', href: '/no-climb-fence' },
      { label: 'Ranch & Horse Fence', href: '/ranch-horse-fence' },
      { label: 'Custom Gates', href: '/custom-gates' },
    ]
  },
  {
    label: 'Land Services', children: [
      { label: 'All Land Services', href: '/land-services' },
      { label: 'Land Clearing', href: '/land-clearing-springtown-tx' },
      { label: 'Dirt Work', href: '/dirt-work-springtown-tx' },
      { label: 'Gravel & Driveways', href: '/gravel-driveways-springtown-tx' },
    ]
  },
  { label: 'Buildings', href: '/metal-buildings' },
  { label: 'Concrete', href: '/concrete' },
  { label: 'Blog', href: '/blog' },
  { label: 'Financing', href: '/financing' },
  { label: 'Contact', scrollTo: 'contact' },
];

const DropdownMenu = ({ item, closeAll }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="text-white hover:text-red-500 transition-colors font-semibold text-sm flex items-center gap-1"
      >
        {item.label} <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-2 w-52 bg-zinc-900 border border-zinc-700/50 rounded-lg shadow-2xl py-2 z-50">
          {item.children.map(child => (
            <Link
              key={child.href}
              to={child.href}
              onClick={() => { setOpen(false); closeAll?.(); }}
              className="block px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors"
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const SiteHeader = ({ mobileMenuOpen, setMobileMenuOpen, scrollToSection }) => {
  const [mobileOpen, setMobileOpen] = useState(mobileMenuOpen || false);
  const [mobileExpanded, setMobileExpanded] = useState(null);

  const handleNav = (item) => {
    if (item.href) {
      window.location.href = item.href;
    } else if (item.scrollTo) {
      if (scrollToSection) scrollToSection(item.scrollTo);
      else window.location.href = `/#${item.scrollTo}`;
    }
    setMobileOpen(false);
    setMobileExpanded(null);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-red-600/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3">
          <Link to="/" className="flex items-center space-x-3 shrink-0">
            <img src={ntxLogo} alt="North Texas Welding and Fabrication Logo" className="h-14 w-auto" data-testid="header-logo" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-5">
            {navItems.map(item =>
              item.children ? (
                <DropdownMenu key={item.label} item={item} />
              ) : item.scrollTo ? (
                <button key={item.label} onClick={() => handleNav(item)} className="text-white hover:text-red-500 transition-colors font-semibold text-sm">
                  {item.label}
                </button>
              ) : (
                <Link key={item.label} to={item.href} className="text-white hover:text-red-500 transition-colors font-semibold text-sm">
                  {item.label}
                </Link>
              )
            )}
            <div className="flex items-center gap-2 ml-3">
              <a
                href="https://www.facebook.com/share/1bUooz3NPo/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-blue-600 transition-colors"
                aria-label="Facebook"
                data-testid="header-facebook-link"
              >
                <Facebook size={16} className="text-white" />
              </a>
              <a
                href="https://share.google/TfFCVxA4nlmSDcgrZ"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-amber-500 transition-colors"
                aria-label="Google Business Profile"
                data-testid="header-google-link"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="white"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              </a>
              <Button
                onClick={() => { trackClick('Pay Invoice', '/'); window.open('https://buy.stripe.com/fZu28qazmeGH1X186Z5ZC03', '_blank'); }}
                variant="outline" size="sm"
                className="border border-zinc-600 text-zinc-300 hover:bg-white hover:text-black font-semibold px-4 py-2 text-xs"
              >
                Pay Invoice
              </Button>
              <Link to="/instant-estimate" onClick={() => trackClick('Instant Estimate - Nav', '/')}>
                <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white font-bold px-5 py-2 text-xs" data-testid="nav-instant-estimate">
                  Instant Estimate
                </Button>
              </Link>
            </div>
          </nav>

          {/* Mobile Toggle */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-white p-2" data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <nav className="lg:hidden pb-4 space-y-1 border-t border-zinc-800 pt-3" data-testid="mobile-nav">
            {navItems.map(item =>
              item.children ? (
                <div key={item.label}>
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                    className="w-full flex justify-between items-center text-white hover:text-red-500 font-semibold py-2.5 text-sm"
                  >
                    {item.label} <ChevronDown size={14} className={`transition-transform ${mobileExpanded === item.label ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileExpanded === item.label && (
                    <div className="pl-4 space-y-1 mb-2">
                      {item.children.map(child => (
                        <Link key={child.href} to={child.href} onClick={() => setMobileOpen(false)} className="block text-zinc-400 hover:text-red-500 py-2 text-sm">
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button key={item.label} onClick={() => handleNav(item)} className="block text-white hover:text-red-500 font-semibold py-2.5 text-sm w-full text-left">
                  {item.label}
                </button>
              )
            )}
            <div className="flex gap-2 pt-2">
              <a
                href="https://www.facebook.com/share/1bUooz3NPo/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-blue-600 transition-colors shrink-0"
                aria-label="Facebook"
                data-testid="mobile-facebook-link"
              >
                <Facebook size={18} className="text-white" />
              </a>
              <a
                href="https://share.google/TfFCVxA4nlmSDcgrZ"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-amber-500 transition-colors shrink-0"
                aria-label="Google Business Profile"
                data-testid="mobile-google-link"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="white"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              </a>
              <Button onClick={() => window.open('https://buy.stripe.com/fZu28qazmeGH1X186Z5ZC03', '_blank')} variant="outline" className="flex-1 border border-zinc-600 text-zinc-300 hover:bg-white hover:text-black font-semibold text-xs">
                Pay Invoice
              </Button>
              <Link to="/instant-estimate" className="flex-1" onClick={() => setMobileOpen(false)}>
                <Button className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs" data-testid="mobile-instant-estimate">
                  Instant Estimate
                </Button>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
